import React, { FC, memo, useState } from "react";
import { styled } from "@mui/material/styles";
import { CardActions, CardContent, Collapse, Typography } from "@mui/material";
import IconButton, { IconButtonProps } from "@mui/material/IconButton";
import { Button } from "components/Shared/Button";
import { useHistory } from "react-router-dom";
import {
  AccessTime,
  Bookmark,
  Category,
  ExpandMore,
  MenuBook,
} from "@mui/icons-material";
import ReactCountryFlag from "react-country-flag";
import { FaHeart, FaRegHeart } from "react-icons/fa";
import { Card } from "components/Shared/Card";
import { useTranslation } from "react-i18next";
import "./styles.scss";
import { useAppDispatch } from "redux/store/hooks";
import { addFavouriteExpert } from "redux/actions/expertsList";
import RoutePaths from "routes/RoutePaths";
import { getLessonById } from "redux/actions/lessons";
import { setSelectedLesson } from "redux/reducers/lessonsSlice";
import LessonInfoPopup from "../ExpertCardNew/LessonInfoPopup";
import { useAuthSelectors } from "redux/selectors";
import { LessonMode } from "features/lessons/models";
import RatingBar from "../../Home/FamousExperts/RatingBar";
import moment from "moment";
import {
  IExpert,
  IExpertArea,
  ILesson,
} from "../../../types/components/expert";
import { ICountry } from "../../../types/redux/home";
import { ILessonMode } from "../../../types/components/expertView";
import {
  IFamouseExpertLessonsTopic,
  ITopic,
} from "../../../types/redux/expert";
import TopicsDescription from "./topicsDescription";
import DateAndTime from "./DateAndTime";

interface ExpandMoreProps extends IconButtonProps {
  expand: boolean;
}

interface IExpertCard {
  expert: IExpert;
}

const ExpandMoreComponent = styled((props: ExpandMoreProps) => {
  const { expand, ...other } = props;
  return <IconButton {...other} />;
})(({ theme, expand }) => ({
  transform: !expand ? "rotate(0deg)" : "rotate(180deg)",
  marginLeft: "auto",
  transition: theme.transitions.create("transform", {
    duration: theme.transitions.duration.shortest,
  }),
}));

const ExpertCard: FC<IExpertCard> = (props) => {
  const { expert } = props;
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [expanded, setExpanded] = useState<boolean>(false);
  const { push } = useHistory();
  const [isLessonInfoModalOpen, setIsLessonInfoModalOpen] =
    useState<boolean>(false);
  const [selectedLessonType, setSelectedLessonType] = useState<any>();
  const { selectAuthUser } = useAuthSelectors();

  const handleExpandClick = () => {
    setExpanded((prev) => !prev);
  };

  const isFavoriteHandler = () => {
    const data = {
      expert_id: expert.id,
      expertName: `${expert.first_name} ${expert.last_name}`,
      isFavoriteExpert: expert.is_favorite,
    };
    dispatch(addFavouriteExpert(data));
  };

  const showInfoHandler = (lesson: any) => {
    dispatch(setSelectedLesson(null));
    const data = {
      lesson_id: lesson.id,
      type: lesson.lesson_mode,
    };
    setSelectedLessonType(lesson.lesson_mode);
    dispatch(getLessonById(data));
    setIsLessonInfoModalOpen(true);
  };

  return (
    <div className="expert">
      <Card>
        <CardContent className="card_content">
          <div className="left_area">
            <img
              src={expert.avatar}
              className="avatar"
              onClick={() =>
                push(`${RoutePaths.selectedExpert}?id=${expert.id}`)
              }
              loading="eager"
              srcSet={expert.avatar + " 1x, " + expert.avatar + " 2x"}
              alt=""
              referrerPolicy="no-referrer"
              data-noaft=""
            />
            <div className="rating_bar">
              <RatingBar
                rating={Math.round(expert.rating * 10) / 10}
              ></RatingBar>
            </div>
            <span className="name">
              {expert.first_name}
              <br />
              {expert.last_name}
            </span>
            <span className="country">
              {t(`countries.${expert.residence}`)}
            </span>
          </div>
          {/*  */}
          <div className="right">
            {/* Like btn */}
            <div className="like_area">
              {" "}
              {selectAuthUser?.logged &&
                selectAuthUser?.expertId !== expert.id && (
                  <React.Fragment>
                    <IconButton onClick={isFavoriteHandler}>
                      {expert?.is_favorite ? (
                        <FaHeart className="like" />
                      ) : (
                        <FaRegHeart className="unlike" />
                      )}
                    </IconButton>
                    <span>{expert.favorite_count}</span>
                  </React.Fragment>
                )}
            </div>
            {/*  */}
            <Typography component="div" className="self-appeal">
              {expert.self_appeal}
            </Typography>
            <div className="row_info countries">
              <span className="label">
                {t("home.famousExperts.ExpertCard.label.expertCountries")}
              </span>
              {expert.expert_country.map((country: ICountry) => {
                return (
                  <span className="expert-country">
                    {t(`countries.${country.name}`)}
                  </span>
                );
              })}
            </div>
            <div className="row_info countries">
              <span className="label">
                {t("home.famousExperts.ExpertCard.label.expertArea")}
              </span>
              {expert.expert_areas.map((area: IExpertArea, index: number) => (
                <span key={index} className="expert-area">
                  {area.area}
                </span>
              ))}
            </div>
            <div className="row_info countries">
              <span className="label">
                {t("home.famousExperts.ExpertCard.label.profession")}
              </span>
              <span className="expert-profession">{expert.profession}</span>
            </div>
            <div className="row_info reg_info">
              <div>
                <span className="label">
                  {t("home.famousExperts.ExpertCard.label.registeredOn")}
                </span>
                <span>{moment(expert.registered_at).format("YYYY-MM-DD")}</span>
              </div>
              <div>
                <span className="label">
                  {t("home.famousExperts.ExpertCard.label.location")}：
                </span>
                <span>{t(`countries.${expert.residence}`)}</span>
              </div>
            </div>
          </div>
        </CardContent>
        <CardActions
          disableSpacing
          className="col_exp_btn"
          onClick={handleExpandClick}
        >
          <div className="center">
            <span>
              {expanded
                ? t("home.famousExperts.ExpertCard.showLess")
                : t("home.famousExperts.ExpertCard.showMore")}
            </span>
            <ExpandMoreComponent
              expand={expanded}
              // onClick={handleExpandClick}
              aria-expanded={expanded}
              aria-label="show more"
            >
              <ExpandMore />
            </ExpandMoreComponent>
          </div>
        </CardActions>
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <CardContent className="expand_content">
            {Object.keys(expert.lessons).map((el: any) =>
              (expert.lessons[el] as unknown as ILesson[]).map(
                (lesson: ILesson) => (
                  <div
                    className="gray_bg"
                    key={lesson.id + lesson.lesson_name + lesson.category_name}
                  >
                    <div
                      className="title_area"
                      // onClick={() => showInfoHandler(lesson)}
                    >
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <h3>{lesson.lesson_name}</h3>
                        <Button
                          id="btn-reserve"
                          label={t(
                            "home.famousExperts.ExpertCard.label.btnReserve"
                          )}
                          onClick={() => showInfoHandler(lesson)}
                        />
                      </div>
                      <h4>{t("categories." + lesson.category_name)}</h4>
                      {lesson.schedule?.schedule_date &&
                        lesson.schedule?.schedule_time && (
                          <DateAndTime
                            date={lesson.schedule?.schedule_date}
                            time={lesson.schedule?.schedule_time}
                          />
                        )}
                      <div className="description">
                        {lesson.lesson_mode === ILessonMode.SINGLE ? (
                          <React.Fragment>{lesson?.description}</React.Fragment>
                        ) : (
                          lesson.topics?.map(
                            (
                              topic: IFamouseExpertLessonsTopic,
                              index: number
                            ) => {
                              return (
                                <TopicsDescription
                                  topic={topic.topic.topic}
                                  description={topic.topic.description}
                                  index={index}
                                  date={topic.topic.schedule?.date}
                                  time={topic.topic.schedule?.time}
                                />
                              );
                            }
                          )
                        )}
                      </div>
                      <div className="country_area">
                        <ReactCountryFlag
                          countryCode={
                            lesson.country_code ? lesson.country_code : ""
                          }
                          svg
                          className="flag"
                        />
                        <div className="country_name">
                          {t("countries." + lesson.country_name)}
                        </div>
                      </div>
                    </div>
                    <div className="info_area">
                      <div className="info_single">
                        <MenuBook />
                        <span>
                          <b>{lesson.topics ? lesson.topics.length : 1}</b>{" "}
                          {t("common.Lesson(s)")}
                        </span>
                      </div>
                      <div className="info_single">
                        <AccessTime />
                        <span>
                          <b>
                            {lesson.session_time}
                            {t("common.min")}
                          </b>
                          /{t("common.lesson")}
                        </span>
                      </div>
                      <div className="info_single">
                        <Bookmark />
                        <span>
                          <b>
                            {" "}
                            {lesson.lesson_mode === LessonMode.PACK &&
                            lesson.topics
                              ? lesson.points * lesson.topics.length
                              : lesson.points}
                          </b>
                          {t("common.Points")}
                        </span>
                      </div>
                      <div className="info_single">
                        <Category />
                        <span>
                          <b>
                            {lesson.lesson_type === 1
                              ? t("common.individual")
                              : t("common.group")}
                          </b>
                        </span>
                      </div>
                    </div>
                  </div>
                )
              )
            )}
          </CardContent>
        </Collapse>
      </Card>
      {isLessonInfoModalOpen && (
        <LessonInfoPopup
          selectedLessonType={selectedLessonType}
          isLessonInfoModalOpen={isLessonInfoModalOpen}
          onCloseHandler={() => {
            setIsLessonInfoModalOpen(false);
          }}
        />
      )}
    </div>
  );
};

export default memo(ExpertCard);
